// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Tooltip } from '@mui/material';
import {
  type TickerTag,
  TickerTagFeature,
} from '@shared/data-access/interfaces/api/engine/citec';
import { PopupSelector } from '@shared/ui-kit/components/popup-selector';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { type FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTagsNames,
  selectWatchlistPositions,
  tagManagerSlice,
} from '@shared/data-access/slices/tag-manager';
import {
  addWatchListPosition,
  removeWatchListPosition,
} from '@shared/data-access/slices/tag-manager/tag-manager-thunk';
import { tickerTagApi } from '@shared/data-access/api/engine/ticker-tag-api';
import { useTranslation } from 'react-i18next';
import type { AppDispatch, AppState } from '@shared/data-access/config/citec-app';

export interface WatchlistToggleButtonProps {
  ticker: string;
  assetType: string;
}

export const WatchlistToggleButton: FC<WatchlistToggleButtonProps> = ({
  ticker,
  assetType,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const watchlist = useSelector(selectWatchlistPositions);
  const tags = useSelector((state: AppState) =>
    selectTagsNames(state, TickerTagFeature.SelectedAssets),
  );

  const [createTickerTag] = tickerTagApi.useCreateTickerTagMutation();

  const isWatchlisted = useMemo(
    () => watchlist.some(item => item.ticker === ticker),
    [watchlist, ticker],
  );

  const toolTipTitle = `${
    isWatchlisted
      ? t('dashboard.remove-from-watchlist', { ns: 'dashboard' })
      : t('dashboard.add-to-watchlist', { ns: 'dashboard' })
  } ${t('dashboard.watchlist', { ns: 'dashboard' })}`;

  const handleToggleWatchlist = useCallback(() => {
    if (isWatchlisted) {
      dispatch(tagManagerSlice.actions.removeWatchlistPosition(ticker));
      void dispatch(removeWatchListPosition({ ticker }));
    } else {
      dispatch(addWatchListPosition({ ticker, assetType }));
    }
  }, [dispatch, isWatchlisted, ticker, assetType]);

  const onSave = useCallback(
    (tags: string[]) => {
      const tagObjects: TickerTag[] = tags
        .filter(tagName => tagName.length > 0)
        .map(tagName => ({
          tickers: [ticker],
          feature: TickerTagFeature.SelectedAssets,
          tag: tagName,
        }));

      if (tagObjects.length === 0) {
        return;
      }

      createTickerTag({
        payload: tagObjects,
      });
    },
    [createTickerTag, ticker],
  );

  return (
    <PopupSelector
      title={t('dashboard.select-tags', { ns: 'dashboard' })}
      options={tags}
      onSave={onSave}
      showPopup={!isWatchlisted}
      placeholder={t('dashboard.select-tags-placeholder', { ns: 'dashboard' })}
    >
      <Tooltip title={toolTipTitle} placement="top">
        <IconButton
          variant="icon"
          icon="Star"
          color={isWatchlisted ? 'main' : 'disabled'}
          filled={isWatchlisted}
          data-testid="watchlist-toggle-button"
          onClick={handleToggleWatchlist}
        />
      </Tooltip>
    </PopupSelector>
  );
};
